import {
  ChangeDetectionStrategy,
  Component,
  Input,
  Optional,
  computed,
  signal
} from '@angular/core';

import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { RouterLink } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

import {
  NavigateListBlock,
  NavigateListItem
} from '@core/services/ai/ai-assistant.service';
import { defaultIconFor } from '../entity-deeplink.helper';

interface ResolvedItem {
  id: string;
  label: string;
  description: string | null;
  icon: string;
  route: string;
  queryParams: Record<string, string> | null;
  tone: string;
}

/**
 * Renders a `navigate-list` widget — a small grid of CTAs the assistant
 * surfaces when several related navigation options are relevant. Reuses
 * the same anti-phishing safeguard as `ai-navigate-action-block`: each
 * row's route MUST start with "/" or it is silently dropped.
 */
@Component({
  selector: 'app-ai-navigate-list-block',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatButtonModule, MatIconModule, RouterLink],
  template: `
    @if (resolvedTitle(); as title) {
      <div class="navlist__title">{{ title }}</div>
    }
    <div class="navlist">
      @for (item of resolvedItems(); track item.id) {
        <a
          class="navlist__item"
          [attr.data-tone]="item.tone"
          [routerLink]="item.route"
          [queryParams]="item.queryParams">
          <mat-icon class="navlist__icon">{{ item.icon }}</mat-icon>
          <div class="navlist__body">
            <div class="navlist__label">{{ item.label }}</div>
            @if (item.description) {
              <div class="navlist__desc">{{ item.description }}</div>
            }
          </div>
          <mat-icon class="navlist__arrow">arrow_forward</mat-icon>
        </a>
      }
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
      }
      .navlist__title {
        font-size: 12px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        color: rgba(15, 23, 42, 0.55);
        margin: 0 0 8px;
      }
      .navlist {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
        gap: 8px;
      }
      .navlist__item {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 10px 12px;
        border-radius: 12px;
        border: 1px solid rgba(99, 102, 241, 0.2);
        background: rgba(99, 102, 241, 0.04);
        color: inherit;
        text-decoration: none;
        transition:
          background 0.15s ease,
          border-color 0.15s ease,
          transform 0.1s ease;
      }
      .navlist__item:hover {
        background: rgba(99, 102, 241, 0.1);
        border-color: rgba(99, 102, 241, 0.45);
        transform: translateY(-1px);
      }
      .navlist__item[data-tone='positive'] {
        border-color: rgba(16, 185, 129, 0.25);
        background: rgba(16, 185, 129, 0.05);
      }
      .navlist__item[data-tone='positive']:hover {
        background: rgba(16, 185, 129, 0.12);
        border-color: rgba(16, 185, 129, 0.5);
      }
      .navlist__item[data-tone='warning'] {
        border-color: rgba(245, 158, 11, 0.25);
        background: rgba(245, 158, 11, 0.05);
      }
      .navlist__item[data-tone='warning']:hover {
        background: rgba(245, 158, 11, 0.12);
        border-color: rgba(245, 158, 11, 0.5);
      }
      .navlist__item[data-tone='danger'] {
        border-color: rgba(239, 68, 68, 0.25);
        background: rgba(239, 68, 68, 0.05);
      }
      .navlist__item[data-tone='danger']:hover {
        background: rgba(239, 68, 68, 0.12);
        border-color: rgba(239, 68, 68, 0.5);
      }
      .navlist__icon {
        flex-shrink: 0;
        font-size: 22px;
        width: 22px;
        height: 22px;
        color: #6366f1;
      }
      .navlist__item[data-tone='positive'] .navlist__icon {
        color: #059669;
      }
      .navlist__item[data-tone='warning'] .navlist__icon {
        color: #d97706;
      }
      .navlist__item[data-tone='danger'] .navlist__icon {
        color: #dc2626;
      }
      .navlist__body {
        flex: 1;
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: 2px;
      }
      .navlist__label {
        font-weight: 600;
        font-size: 14px;
        color: rgba(15, 23, 42, 0.92);
        overflow-wrap: anywhere;
      }
      .navlist__desc {
        font-size: 12px;
        color: rgba(15, 23, 42, 0.6);
        line-height: 1.35;
        overflow-wrap: anywhere;
      }
      .navlist__arrow {
        flex-shrink: 0;
        font-size: 16px;
        width: 16px;
        height: 16px;
        color: rgba(15, 23, 42, 0.4);
      }
    `
  ]
})
export class AiNavigateListBlockComponent {
  private readonly _block = signal<NavigateListBlock | null>(null);

  constructor(
    @Optional() private readonly translate: TranslateService | null = null
  ) {}

  @Input({ required: true }) set block(value: NavigateListBlock) {
    this._block.set(value);
  }
  get block(): NavigateListBlock {
    return this._block()!;
  }

  readonly resolvedTitle = computed<string | null>(() => {
    const b = this._block();
    if (!b) return null;
    if (!b.title && !b.titleKey) return null;
    return this.resolveKey(b.titleKey, b.title ?? '') || null;
  });

  /**
   * Drop items whose route is not in-app (anti-phishing). Coerce query
   * params to strings, resolve labels via i18n. Cap at 6 items so the
   * grid stays scannable.
   */
  readonly resolvedItems = computed<ResolvedItem[]>(() => {
    const b = this._block();
    if (!b) return [];
    const out: ResolvedItem[] = [];
    for (const item of b.items) {
      if (typeof item.route !== 'string' || !item.route.startsWith('/'))
        continue;
      out.push({
        id: item.id,
        label: this.resolveKey(item.labelKey, item.label),
        description:
          item.description || item.descriptionKey
            ? this.resolveKey(item.descriptionKey, item.description ?? '') ||
              null
            : null,
        icon: this.iconFor(item),
        route: item.route,
        queryParams: this.normalizeQueryParams(item.queryParams),
        tone: item.tone || 'neutral'
      });
      if (out.length >= 6) break;
    }
    return out;
  });

  private iconFor(item: NavigateListItem): string {
    if (item.icon) return item.icon;
    if (item.entityType) return defaultIconFor(item.entityType);
    return 'open_in_new';
  }

  private normalizeQueryParams(
    raw: Record<string, string | number | boolean> | undefined
  ): Record<string, string> | null {
    if (!raw) return null;
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(raw)) {
      if (v === null || v === undefined) continue;
      out[k] = String(v);
    }
    return Object.keys(out).length > 0 ? out : null;
  }

  private resolveKey(key: string | undefined, fallback: string): string {
    if (!key) return fallback;
    if (!this.translate) return fallback;
    const translated = this.translate.instant(key);
    if (typeof translated !== 'string' || translated === key) return fallback;
    return translated;
  }
}
